import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { shortAddress, shortCode } from "@tenergy/web-core/format";
import { requestIdOf, slugOf } from "./api/request.js";

/**
 * The panel's parts, in the platform's dark system (site-design.md §2, §6, §8), one step denser
 * than the dashboard: a landing page is read once, a dashboard is worked in, and an admin panel
 * is lived in.
 *
 *   card      `surface` + hairline + 20 px radius, two nesting levels at most;
 *   control   12 px radius, `surface-2` fields, accent fill for the primary button;
 *   numbers   JetBrains Mono everywhere (`.num`), right-aligned in tables;
 *   headers   uppercase 11 px mono in tables and stat labels;
 *   colour    accent for action and focus, mint (`save`) only for healthy/delivered, amber
 *             (`warning`) only for attention, red for errors.
 *
 * TABLE FIRST. Almost every screen here is rows of facts, so `Table` has the most care in it:
 * one scroll container so the page body never scrolls sideways, and an explicit empty row that
 * says what would put a row here rather than a collapsed `<tbody>`.
 */

export function Panel({
  title,
  subtitle,
  action,
  children,
  class: className = "",
  flush = false,
}: {
  title?: string | undefined;
  subtitle?: string | undefined;
  action?: ComponentChildren;
  children: ComponentChildren;
  class?: string;
  /** No inner padding: for a table that runs edge to edge. */
  flush?: boolean;
}) {
  return (
    <section class={`rounded-[20px] border border-line bg-surface ${className}`}>
      {title === undefined && action === undefined ? null : (
        <header class="flex flex-wrap items-center justify-between gap-2 px-4 pt-3.5 pb-2.5">
          <div class="min-w-0">
            {title === undefined ? null : (
              <h2 class="font-display text-[14px] font-extrabold tracking-[-0.01em] text-ink">{title}</h2>
            )}
            {subtitle === undefined ? null : (
              <p class="mt-0.5 max-w-3xl text-[11.5px] leading-relaxed text-ink-muted">{subtitle}</p>
            )}
          </div>
          {action}
        </header>
      )}
      <div class={flush ? "pb-1" : "px-4 pb-4 pt-1"}>{children}</div>
    </section>
  );
}

export function ScreenHeader({
  title,
  lead,
  action,
}: {
  title: string;
  lead?: string | undefined;
  action?: ComponentChildren;
}) {
  return (
    <div class="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div class="min-w-0">
        <h1 class="font-display text-[24px] leading-tight font-extrabold tracking-[-0.02em] text-ink">
          {title}
        </h1>
        {lead === undefined ? null : (
          <p class="mt-1 max-w-3xl text-[12px] leading-relaxed text-ink-muted">{lead}</p>
        )}
      </div>
      {action}
    </div>
  );
}

type ButtonTone = "primary" | "ghost" | "danger";

const TONE: Record<ButtonTone, string> = {
  primary: "bg-accent text-accent-contrast hover:bg-accent-hover",
  ghost: "border border-line bg-surface text-ink hover:border-accent/60",
  danger: "border border-danger/50 text-danger hover:bg-danger/10",
};

/**
 * A disabled PRIMARY button is a flat neutral block, not the accent at half opacity: half of a
 * saturated blue on a dark surface still reads as "press me", and the button that says "Enter
 * a reason" must not look like the one that publishes a price.
 */
const DISABLED_TONE = "bg-surface-raised text-ink-muted border border-line";

export function Button({
  tone = "primary",
  type = "button",
  disabled = false,
  onClick,
  children,
  class: className = "",
  title,
}: {
  tone?: ButtonTone;
  type?: "button" | "submit";
  disabled?: boolean;
  onClick?: (event: MouseEvent) => void;
  children: ComponentChildren;
  class?: string;
  title?: string | undefined;
}) {
  return (
    <button
      type={type}
      disabled={disabled}
      {...(title === undefined ? {} : { title })}
      onClick={onClick}
      class={`inline-flex min-h-8 items-center justify-center gap-1.5 rounded-[12px] px-3 text-[12px] font-medium transition-colors duration-150 disabled:cursor-not-allowed ${
        disabled ? DISABLED_TONE : TONE[tone]
      } ${className}`}
    >
      {children}
    </button>
  );
}

export const inputClass =
  "w-full min-h-8 rounded-[12px] border border-line bg-surface-raised px-2.5 text-[12px] text-ink placeholder:text-ink-muted/80 focus:border-accent focus:outline-none";

/**
 * A button in a row of Fields, level with their inputs: an empty line the height of a Field's label above it. Aligned
 * to the cell's bottom instead, it sat between an input and its hint (owner 2026-10-24: «вёрстка кнопок кривая»).
 */
export function FieldAction({ children }: { children: ComponentChildren }) {
  return (
    <div>
      <span aria-hidden="true" class="invisible mb-1 block text-[11px] font-medium">
        .
      </span>
      {children}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | null | undefined;
  children: ComponentChildren;
}) {
  // Hint and error sit OUTSIDE the `<label>`: a label wrapping its own help text makes the
  // field's accessible name the label plus the help text, which is what a screen reader reads.
  return (
    <div>
      <label class="block">
        <span class="mb-1 block text-[11px] font-medium text-ink">{label}</span>
        {children}
      </label>
      {hint === undefined ? null : (
        <span class="mt-1 block text-[11px] leading-relaxed text-ink-muted">{hint}</span>
      )}
      {error ? (
        <span role="alert" class="mt-1 block text-[11px] text-danger">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function Table({
  head,
  children,
  empty,
  rows,
  action,
}: {
  head: ComponentChildren;
  children: ComponentChildren;
  /** What an empty table says: one sentence, never "no data" alone. */
  empty: string;
  /** How many rows `children` renders. Drives the empty state without inspecting children. */
  rows: number;
  /** The next step an empty table offers, rendered under the sentence. */
  action?: ComponentChildren;
}) {
  return (
    <div class="table-scroll -mx-4 last:-mb-4">
      <table class="w-full border-collapse text-left text-[12px]">
        <thead class="num text-[10.5px] tracking-[0.06em] text-ink-muted uppercase">{head}</thead>
        <tbody>
          {rows === 0 ? (
            <tr>
              <td class="px-4 py-7 text-center" colSpan={99}>
                <p class="mx-auto max-w-xl text-[12px] leading-relaxed text-ink-muted">{empty}</p>
                {action === undefined ? null : <div class="mt-2.5 flex justify-center">{action}</div>}
              </td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Th({
  children,
  numeric = false,
}: {
  children: ComponentChildren;
  numeric?: boolean;
}) {
  return (
    <th
      scope="col"
      class={`border-b border-line px-4 py-2 font-medium whitespace-nowrap first:pl-4 ${
        numeric ? "text-right" : ""
      }`}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  numeric = false,
  mono = false,
  class: className = "",
}: {
  children: ComponentChildren;
  numeric?: boolean;
  mono?: boolean;
  class?: string;
}) {
  return (
    <td
      class={`border-b border-line px-4 py-2 align-top text-ink ${
        numeric ? "num text-right whitespace-nowrap" : ""
      } ${mono ? "num text-[11.5px] whitespace-nowrap" : ""} ${className}`}
    >
      {children}
    </td>
  );
}

/**
 * STATUS PILLS. Four tones and no more: mint for healthy/delivered, amber for attention, red
 * for failure, accent for "in flight", and neutral for everything that is just a label.
 */
const GOOD = "bg-save/12 text-save";
const ATTENTION = "bg-heat/12 text-warning";
const BAD = "bg-danger/12 text-danger";
const MOVING = "bg-accent-soft text-accent-text";

const STATE_TONE: Record<string, string> = {
  active: GOOD,
  confirmed: GOOD,
  delivered: GOOD,
  verified: GOOD,
  healthy: GOOD,
  reclaimed: GOOD,
  completed: GOOD,
  lent: GOOD,
  instant: MOVING,
  paid: MOVING,
  allocating: MOVING,
  delegated: MOVING,
  broadcast: MOVING,
  intent: MOVING,
  reclaiming: MOVING,
  market: ATTENTION,
  deep: ATTENTION,
  suspended: ATTENTION,
  paused: ATTENTION,
  warning: ATTENTION,
  draining: ATTENTION,
  refunded: ATTENTION,
  overdue: ATTENTION,
  reclaim_due: ATTENTION,
  partial: ATTENTION,
  unhealthy: BAD,
  failed: BAD,
  critical: BAD,
  quarantined: BAD,
  page: BAD,
  expired_unreclaimable: BAD,
};

/**
 * Order statuses shown in words people read (owner 2026-10-11): `expired` / `reclaimed` mean the rental ran its
 * whole term and the energy came back — a completed order, not a failure. The API keeps its own values.
 */
export const ORDER_STATUS_LABEL: Record<string, string> = { expired: "completed", reclaimed: "completed" };

export function Pill({ value, tone }: { value: string; tone?: "good" | "attention" | "bad" | "moving" | "neutral" }) {
  const cls =
    tone === "good"
      ? GOOD
      : tone === "attention"
        ? ATTENTION
        : tone === "bad"
          ? BAD
          : tone === "moving"
            ? MOVING
            : tone === "neutral"
              ? "bg-surface-raised text-ink-muted"
              : (STATE_TONE[value] ?? "bg-surface-raised text-ink-muted");
  return (
    <span
      class={`num inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] leading-4 whitespace-nowrap ${cls}`}
    >
      {value}
    </span>
  );
}

/**
 * A STAT CARD (the TronSave pattern, site-design.md §6): uppercase mono label, a mono number,
 * a sub-line, a glyph at the right.
 *
 * `value === null` is **no data** — a card never invents a number, and the `note` says why it
 * is empty, so an operator can tell a broken scrape from a series that has never been written.
 */
export function Tile({
  label,
  value,
  unit,
  note,
  tone = "neutral",
  icon,
}: {
  label: string;
  value: string | null;
  unit?: string | undefined;
  note?: string | null | undefined;
  tone?: "neutral" | "warning" | "danger" | "good";
  icon?: IconName;
}) {
  const valueTone =
    value === null
      ? "text-ink-muted"
      : tone === "danger"
        ? "text-danger"
        : tone === "warning"
          ? "text-warning"
          : tone === "good"
            ? "text-save"
            : "text-ink";
  return (
    <div class="relative rounded-[20px] border border-line bg-surface px-4 py-3.5">
      <div class="flex items-start justify-between gap-2">
        <div class="num text-[10.5px] tracking-[0.08em] text-ink-muted uppercase">{label}</div>
        {icon === undefined ? null : (
          <span class="flex size-7 shrink-0 items-center justify-center rounded-[10px] bg-accent-soft text-accent-text">
            <Icon name={icon} />
          </span>
        )}
      </div>
      <div class={`num mt-1.5 text-[22px] leading-none font-semibold tracking-[-0.02em] ${valueTone}`}>
        {value === null ? <span class="text-[13px] font-normal">no data</span> : value}
        {value !== null && unit !== undefined ? (
          <span class="ml-1 text-[11px] font-normal text-ink-muted">{unit}</span>
        ) : null}
      </div>
      {note ? <div class="mt-2 text-[10.5px] leading-relaxed text-ink-muted">{note}</div> : null}
    </div>
  );
}

/** The stat-card row at the top of a screen. */
export function StatRow({ children }: { children: ComponentChildren }) {
  return <div class="mb-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{children}</div>;
}

export function Note({
  tone = "info",
  children,
}: {
  tone?: "info" | "warning" | "danger";
  children: ComponentChildren;
}) {
  const style =
    tone === "danger"
      ? "border-danger/40 bg-danger/8 text-danger"
      : tone === "warning"
        ? "border-heat/40 bg-heat/8 text-warning"
        : "border-accent/30 bg-accent-soft/40 text-ink-muted";
  return (
    <p
      {...(tone === "danger" ? { role: "alert" } : {})}
      class={`rounded-[12px] border px-3 py-2 text-[11.5px] leading-relaxed ${style}`}
    >
      {children}
    </p>
  );
}

/**
 * A failed read, said with what finds it in the logs: the API's message, its slug and the
 * request id. Inline rather than a toast, because it stays true until the screen is reloaded.
 */
export function ErrorNote({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : String(error);
  const slug = slugOf(error);
  const requestId = requestIdOf(error);
  return (
    <div
      role="alert"
      class="flex flex-wrap items-center justify-between gap-2 rounded-[12px] border border-danger/40 bg-danger/8 px-3 py-2"
    >
      <div class="min-w-0 text-[11.5px] leading-relaxed text-danger">
        {message}
        {slug === null && requestId === null ? null : (
          <span class="num ml-2 text-[10.5px] text-ink-muted">
            {[slug, requestId].filter((part) => part !== null).join(" · ")}
          </span>
        )}
      </div>
      {onRetry === undefined ? null : (
        <Button tone="ghost" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

/**
 * An EMPTY STATE: one sentence about what would be here, and the next action when there is one.
 * Never a zero standing in for "nothing measured" (site-design.md §6, "not taken").
 */
export function EmptyState({ children, action }: { children: ComponentChildren; action?: ComponentChildren }) {
  return (
    <div class="rounded-[12px] border border-dashed border-line px-4 py-5 text-center">
      <p class="mx-auto max-w-xl text-[12px] leading-relaxed text-ink-muted">{children}</p>
      {action === undefined ? null : <div class="mt-2.5 flex justify-center">{action}</div>}
    </div>
  );
}

/**
 * Something no endpoint publishes. Said out loud rather than left as a blank panel, because a
 * blank panel reads as "nothing is happening" and this means "nobody can see it from here".
 */
export function NotAvailable({ what, why }: { what: string; why: string }) {
  return (
    <EmptyState>
      <span class="font-medium text-ink">{what}</span> is not published by the API. {why}
    </EmptyState>
  );
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <p role="status" class="py-6 text-center text-[11.5px] text-ink-muted">
      {label}
    </p>
  );
}

export function CopyButton({ value, label = "copy" }: { value: string; label?: string }) {
  const [done, setDone] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );
  return (
    <button
      type="button"
      title={value}
      aria-label={`Copy ${value}`}
      class="inline-flex shrink-0 items-center rounded px-1 text-[10px] text-ink-muted underline decoration-dotted underline-offset-2 hover:text-accent-text"
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(
          () => {
            setDone(true);
            timer.current = setTimeout(() => setDone(false), 1_500);
          },
          () => undefined,
        );
      }}
    >
      {done ? "copied" : label}
    </button>
  );
}

/** A typed platform id (`ord_…`, `acc_…`) as its short code, full value one press away. */
export function ShortId({ value }: { value: string }) {
  return (
    <span class="inline-flex items-center gap-1 whitespace-nowrap">
      <span class="num text-[11.5px] text-ink" title={value}>
        {shortCode(value)}
      </span>
      <CopyButton value={value} />
    </span>
  );
}

/**
 * A TRON address. NOT `ShortId`: `shortCode` keeps the last eight characters and prefixes a
 * `#`, which is right for a ULID nobody reads and wrong for an address, where the leading `T`
 * and the first characters are what an operator recognises and compares against an explorer.
 */
export function Address({ value }: { value: string }) {
  return (
    <span class="inline-flex items-center gap-1 whitespace-nowrap">
      <span class="num text-[11.5px] text-ink" title={value}>
        {shortAddress(value)}
      </span>
      <CopyButton value={value} />
    </span>
  );
}

const SIGN_IN_LABEL = { wallet: "Wallet", google: "Google", email: "Email", telegram: "Telegram" } as const;

/**
 * The ways into an account (owner 2026-10-17): a wallet as an address, the rest as `Google al****@gmail.com`.
 * E-mails and Telegram names come masked for a viewer from the API; a masked value has nothing to copy.
 */
export function SignInMethods({
  methods,
  detailed = false,
}: {
  methods: ReadonlyArray<{ kind: keyof typeof SIGN_IN_LABEL; value: string | null; role: string; last_used_at: string | null }>;
  detailed?: boolean;
}) {
  return (
    <span class={detailed ? "flex flex-wrap gap-x-4 gap-y-1" : "flex flex-col gap-0.5"}>
      {methods.map((m) => (
        <span key={`${m.kind}:${m.value ?? ""}`} class="inline-flex items-center gap-1 whitespace-nowrap text-[11.5px]">
          {m.kind === "wallet" && m.value !== null ? (
            <Address value={m.value} />
          ) : (
            <>
              <span class="text-ink-muted">{SIGN_IN_LABEL[m.kind]}</span>
              <span class="num text-ink" title={m.value ?? undefined}>
                {m.value ?? "—"}
              </span>
              {m.value !== null && !m.value.includes("****") ? <CopyButton value={m.value} /> : null}
            </>
          )}
          {detailed && m.last_used_at !== null ? <span class="text-ink-muted">last used {m.last_used_at.slice(0, 10)}</span> : null}
        </span>
      ))}
    </span>
  );
}

/** A chain transaction id, shortened the way an explorer shows it. */
export function TxId({ value }: { value: string }) {
  return (
    <span class="inline-flex items-center gap-1 whitespace-nowrap">
      <span class="num text-[11.5px] text-ink" title={value}>
        {value.length > 16 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value}
      </span>
      <CopyButton value={value} />
    </span>
  );
}

/** `—` for a value the row genuinely does not have. One glyph, one meaning, everywhere. */
export function Blank({ title }: { title?: string | undefined }) {
  return (
    <span class="text-ink-muted" {...(title === undefined ? {} : { title })}>
      —
    </span>
  );
}

/** A labelled fact inside a detail panel. */
export function Fact({ label, value, mono = false }: { label: string; value: ComponentChildren; mono?: boolean }) {
  return (
    <div class="flex justify-between gap-3 border-b border-line py-1.5">
      <dt class="text-[11.5px] text-ink-muted">{label}</dt>
      <dd class={`text-right text-[12px] text-ink ${mono ? "num break-all" : ""}`}>{value}</dd>
    </div>
  );
}

// ---- glyphs: inline SVG, 16 px, stroke only. No icon library for a dozen shapes. ------------

export type IconName =
  | "pulse"
  | "stack"
  | "book"
  | "tag"
  | "doc"
  | "list"
  | "user"
  | "chart"
  | "bell"
  | "shield"
  | "bolt"
  | "clock"
  | "queue"
  | "wallet";

const PATHS: Record<IconName, string> = {
  pulse: "M2 8h3l2-5 2 10 2-5h3",
  stack: "M8 2 2 5l6 3 6-3-6-3ZM2 8l6 3 6-3M2 11l6 3 6-3",
  book: "M3 3h4a2 2 0 0 1 2 2v9a2 2 0 0 0-2-2H3ZM13 3H9v11a2 2 0 0 1 2-2h2Z",
  tag: "M2 2h6l6 6-6 6-6-6ZM5 5h.01",
  doc: "M4 2h5l3 3v9H4ZM9 2v3h3M6 8h4M6 11h4",
  list: "M5 4h9M5 8h9M5 12h9M2 4h.01M2 8h.01M2 12h.01",
  user: "M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM2 14c1-3 3.5-4 6-4s5 1 6 4",
  chart: "M2 14h12M4 11V7M8 11V3M12 11V6",
  bell: "M4 11V7a4 4 0 0 1 8 0v4l1 1H3ZM7 14h2",
  shield: "M8 2 3 4v4c0 3 2 5 5 6 3-1 5-3 5-6V4Z",
  bolt: "M9 1 3 9h4l-1 6 6-8H8Z",
  clock: "M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12ZM8 5v3l2 2",
  queue: "M2 4h12v3H2ZM2 9h12v3H2Z",
  wallet: "M2 5h11v8H2ZM2 5l9-2v2M10 9h1",
};

export function Icon({ name, class: className = "" }: { name: IconName; class?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      class={className}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
